import * as THREE from 'three';
import { AnimationData } from '../loaders/zmo';
import { EventEmitter } from "../util/eventemitter";

export class Animator extends EventEmitter {
    objects: any[];
    data: any;
    currentTime: number;
    isPlaying: boolean;
    isPaused: boolean;
    loop: number;
    timeScale: number;
    length: number;
    weight: number;

    constructor(objects: any[], animationData: any) {
        super();
        this.objects = objects;
        this.data = animationData;
        this.currentTime = 0;
        this.isPlaying = false;
        this.isPaused = false;
        this.loop = 0;
        this.timeScale = 1;
        this.length = this.data.frameCount / this.data.fps;
        this.weight = 1;
    }

    play(startTime?: number, weight?: number): void {
        if (this.isPlaying) {
            this.stop();
        }

        this.currentTime = startTime !== undefined ? startTime : 0;
        this.weight = weight !== undefined ? weight : 1;

        this.reset();

        this.isPlaying = true;

        (THREE as any).AnimationHandler.play(this);
    }

    pause(): void {
        this.isPaused = true;
    }

    unpause(): void {
        this.isPaused = false;
    }

    stop(): void {
        if (!this.isPlaying) {
            return;
        }

        this.isPlaying = false;
        this.isPaused = false;

        (THREE as any).AnimationHandler.stop(this);
    }

    reset(): void {
        for (let h = 0, hl = this.objects.length; h < hl; h++) {
            let object = this.objects[h];

            object.matrixAutoUpdate = true;

            if (object.animatorCache === undefined) {
                object.animatorCache = {
                    blending: {
                        positionWeight: 0.0,
                        quaternionWeight: 0.0,
                        scaleWeight: 0.0,
                    },
                };
            }
        }
    }

    resetBlendWeights(): void {
        for (let h = 0, hl = this.objects.length; h < hl; h++) {
            let object = this.objects[h];

            if (object.animatorCache !== undefined) {
                object.animatorCache.blending.positionWeight = 0.0;
                object.animatorCache.blending.quaternionWeight = 0.0;
                object.animatorCache.blending.scaleWeight = 0.0;
            }
        }
    }

    private emitFrameEvents(fromTime: number, toTime: number): void {
        const events = this.data.eventFrames;
        const fps = this.data.fps;
        if (!events || events.length === 0 || !fps || toTime < fromTime) {
            return;
        }

        const firstFrame = Math.max(0, Math.floor(fromTime * fps) + 1);
        const lastFrame = Math.min(events.length - 1, Math.floor(toTime * fps));
        for (let frame = firstFrame; frame <= lastFrame; frame++) {
            const eventCode = events[frame];
            if (eventCode) {
                this.emit("frame_event", eventCode, frame);
            }
        }
    }

    _applyChannel(index: number, type: any, value: any): void {
        const object = this.objects[index];
        const blending = object.animatorCache.blending;

        // Skip if value is undefined or null
        if (value === undefined || value === null) {
            return;
        }

        object.matrixAutoUpdate = true;
        object.matrixWorldNeedsUpdate = true;

        if (type === AnimationData.CHANNEL_TYPE.Position) {
            if (blending.positionWeight === 0) {
                object.position.copy(value);
                blending.positionWeight = this.weight;
            } else {
                let proportionalWeight =
                    this.weight / (this.weight + blending.positionWeight);
                object.position.lerp(value, proportionalWeight);
                blending.positionWeight += this.weight;
            }
        } else if (
            type === AnimationData.CHANNEL_TYPE.Rotation
        ) {
            if (blending.quaternionWeight === 0) {
                object.quaternion.copy(value);
                blending.quaternionWeight = this.weight;
            } else {
                let proportionalWeight =
                    this.weight / (this.weight + blending.quaternionWeight);
                object.quaternion.slerp(value, proportionalWeight);
                blending.quaternionWeight += this.weight;
            }
        } else if (
            type === AnimationData.CHANNEL_TYPE.Scale
        ) {
            if (blending.scaleWeight === 0) {
                object.scale.copy(value);
                blending.scaleWeight = this.weight;
            } else {
                const proportionalWeight =
                    this.weight / (this.weight + blending.scaleWeight);
                object.scale.lerp(value, proportionalWeight);
                blending.scaleWeight += this.weight;
            }
        }
    }

    _applyChannels(
        channels: any[],
        thisFrame: number,
        nextFrame: number,
        frameRatio: number
    ): void {
        for (let i = 0; i < channels.length; ++i) {
            const c = channels[i];

            const f0 = c.frames[thisFrame];
            const f1 = c.frames[nextFrame];
            let f = null;
            if (frameRatio === 0) {
                f = f0;
            } else {
                if (f0 instanceof THREE.Quaternion) {
                    _SLERPER.slerpQuaternions(
                        f0,
                        f1,
                        frameRatio
                    );
                    f = _SLERPER;
                } else if (f0 instanceof THREE.Vector3) {
                    f = _V3LERPER.copy(f0).lerp(f1, frameRatio);
                } else if (f0 instanceof THREE.Vector2) {
                    f = _V2LERPER.copy(f0).lerp(f1, frameRatio);
                } else if (typeof f0 === "number") {
                    f = f0 * (1 - frameRatio) + f1 * frameRatio;
                } else {
                    f = f0;
                }
            }
            this._applyChannel(c.index, c.type, f);
        }
    }

    update(delta: number): number {
        if (this.isPlaying === false) return delta;

        if (!this.isPaused) {
            const previousTime = this.currentTime;
            this.currentTime += delta * this.timeScale;

            // If this is the last frame, we have to stop at the last frame, rather
            //   then blending back towards frame 0, remove that time.
            let endTime = this.length;
            if (this.loop === 1) {
                endTime = (this.data.frameCount - 1) / this.data.fps;
            }

            this.emitFrameEvents(previousTime, Math.min(this.currentTime, endTime));

            if (this.currentTime >= endTime || this.currentTime < 0) {
                if (this.loop === 0 || this.loop > 1) {
                    if (this.loop !== 0) {
                        this.loop--;
                    }

                    this.currentTime %= this.length;

                    if (this.currentTime < 0) {
                        this.currentTime += this.length;
                    }

                    this.reset();
                    this.emit("restart");
                } else {
                    const timeConsumed = endTime - this.currentTime;
                    this.currentTime = endTime;
                    this.pause();
                    this.emit("finish");
                    return delta - timeConsumed;
                }
            }
        }

        if (this.weight === 0) {
            return 0.0;
        }

        const thisFrame = Math.floor(this.currentTime * this.data.fps);
        // TODO: THIS SHOULD NOT BE NEEDED
        let nextFrame = thisFrame + 1;
        if (nextFrame >= this.data.frameCount) {
            if (this.loop) {
                nextFrame -= this.data.frameCount;
            } else {
                nextFrame = thisFrame;
            }
        }

        const frameRatio =
            (this.currentTime - thisFrame / this.data.fps) /
            (1 / this.data.fps);
        this._applyChannels(
            this.data.channels,
            thisFrame,
            nextFrame,
            frameRatio
        );

        return 0.0;
    }
}

const _SLERPER = new THREE.Quaternion();
const _V3LERPER = new THREE.Vector3();
const _V2LERPER = new THREE.Vector2();
